import * as THREE from 'three';
import type { Vessel, FallenSpar } from '../../sim/vessel';
import { mast, mastXAt } from '../../data/ship';
import { Quat, Vec3 } from '../../core/math';
import { Batch, cachedBatch } from './geom';
import { buildHull, lowPolyDeck, lowPolyHull, portRects } from './hullMesh';
import { Rig, buildFallenSpar } from './rig';
import { Fittings } from './fittings';
import {
  BURN_STATIONS, MAX_BREACHES, MAX_HOLES, PORTS, createLowMaterials, createShipMaterials, makeShipUniforms, materialFamily,
  type LowShipMaterials, type ShipLook, type ShipMaterials, type ShipUniforms,
} from '../materials/materials';
import { TextureLibrary } from '../materials/textures';
import { DYNAMIC_NODES, instantiate, type Art } from './art';

/** A ship's hull as the model needs it: static parts, the rudder on its pivot, the wheel. */
export interface HullParts {
  group: THREE.Group;
  rudder: THREE.Object3D;
  rudderPos: THREE.Vector3;
  rudderQuat: THREE.Quaternion;
  /** The Blender-built wheel (turns about its +X), if the hull came from art. */
  wheel: { node: THREE.Object3D; position: THREE.Vector3 } | null;
  /** Deck furniture came with the hull (the Blender model), so don't build it. */
  withFittings: boolean;
  /** The model's deck furniture (dropped on other ships at a distance). */
  furniture: THREE.Object3D | null;
  /** Middle distance: a lighter hull, and the full one's nodes it replaces. */
  lod: { mid: THREE.Object3D; full: THREE.Object3D[] } | null;
}

/** Shared by every ship on the water: textures, the Blender models and cached hull geometry. */
export class ShipAssets {
  readonly tex: TextureLibrary;
  private hullInfo: { rudder: THREE.BufferGeometry; rudderPivot: THREE.Vector3; rudderAxis: THREE.Vector3 } | null = null;

  constructor(renderer: THREE.WebGLRenderer, hiRes: boolean, readonly art: Art | null = null) {
    this.tex = new TextureLibrary(renderer, hiRes);
    PORTS.splice(0, PORTS.length, ...portRects());
  }

  /** `named`: carry the name on her stern (the model's is our own ship's). */
  hull(mats: ShipMaterials, classId: string, named: boolean): HullParts {
    const set = this.art?.ships.get(classId);
    const r = set?.node('rudder');
    if (set && r) {
      // The Blender model: every node but the animated ones is static.
      const group = new THREE.Group();
      let furniture: THREE.Object3D | null = null;
      const full: THREE.Object3D[] = [];
      for (const n of set.nodes) {
        if (DYNAMIC_NODES.has(n.name) || (n.name === 'nameboard' && !named)) continue;
        const o = instantiate(n, mats);
        if (n.name === 'fittings') furniture = o;
        if (n.name === 'hull' || n.name === 'nameboard') full.push(o);
        group.add(o);
      }
      const lodNode = set.node('hull_lod');
      let lod: HullParts['lod'] = null;
      if (lodNode) {
        const mid = instantiate(lodNode, mats);
        mid.visible = false;
        group.add(mid);
        lod = { mid, full };
      }
      const w = set.node('wheel');
      return {
        group, rudder: instantiate(r, mats, true), rudderPos: r.position.clone(), rudderQuat: r.quaternion.clone(),
        wheel: w ? { node: instantiate(w, mats, true), position: w.position.clone() } : null, withFittings: true, furniture, lod,
      };
    }
    const group = cachedBatch('hull', (b) => (this.hullInfo = buildHull(b)), mats);
    const info = this.hullInfo!;
    const mesh = new THREE.Mesh(info.rudder, mats.hull);
    mesh.castShadow = mesh.receiveShadow = true;
    return {
      group, rudder: mesh, rudderPos: info.rudderPivot.clone(),
      rudderQuat: new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), info.rudderAxis), wheel: null, withFittings: false,
      furniture: null, lod: null,
    };
  }
}

function lookFor(v: Vessel): ShipLook {
  const p = v.cls.paint;
  const canvas = v.faction === 'navy' ? new THREE.Color(1.02, 1.0, 0.96)
    : v.faction === 'merchant' ? new THREE.Color(0.9, 0.84, 0.72)
    : v.faction === 'player' ? new THREE.Color(1, 1, 1)
    : new THREE.Color(0.78, 0.7, 0.6);
  return {
    topside: new THREE.Color(...p.hull),
    strake: new THREE.Color(...p.strake),
    canvas,
    flag: v.faction === 'navy' ? 'navy' : v.faction === 'merchant' ? 'merchant' : 'pirate',
  };
}

interface FallenView {
  spar: FallenSpar;
  group: THREE.Group;
  pivot: THREE.Group;
  cut: number;
  splashed: boolean;
}

/** A splash or crash the model noticed (spar hitting the water). */
export interface ModelEvent {
  x: number;
  y: number;
  z: number;
  size: number;
}

const tmpV = new Vec3();
const tmpQ = new Quat();

/**
 * Detail goes by how big a ship looks: her length on screen in pixels, at
 * the camera's present field of view and the render resolution. So zooming
 * in brings it back, and a small window or a dropped render scale sheds it
 * sooner. Other ships shorter than BASIC_PX are drawn as their basic build
 * only: the hull's outer shell, spars and canvas in plain colours, with no
 * crew, rigging, deck fittings, guns, interior or textures (all smaller
 * than a pixel by then, and side by side the two can't be told apart).
 * Each step has a margin so a ship on the line doesn't flicker.
 */
const BASIC_PX = 40, BASIC_PX_OUT = 46;
/** Under this other ships take the model's lighter hull. */
const HULL_MID_PX = 90, HULL_MID_PX_OUT = 100;
/** Under these the deck furniture, then the fittings (guns, props) and running rigging, are left off. */
const FURNITURE_PX = 70;
const FITTINGS_PX = 55;

/**
 * One ship. `posed` follows the sim pose in unscaled ship-local metres (crew
 * and effects attach here); `root` below it is scaled to the ship's class, so
 * everything built from the design hull lines fits.
 */
export class ShipModel {
  readonly posed = new THREE.Group();
  readonly root = new THREE.Group();
  readonly materials: ShipMaterials;
  readonly uniforms: ShipUniforms;
  private readonly rudder: THREE.Object3D;
  private readonly rudderPivot: THREE.Group;
  private readonly hull: THREE.Group;
  private readonly furniture: THREE.Object3D | null;
  private readonly hullLod: HullParts['lod'];
  private midNow = false;
  private readonly look: ShipLook;
  private lowHull: THREE.Group | null = null;
  private lowMats: LowShipMaterials | null = null;
  private lowNow = false;
  private rigPieces = 0;
  readonly rig: Rig;
  readonly fittings: Fittings;
  private readonly lights: THREE.PointLight[] = [];
  private readonly lightAnchors: number[][] = [[0, 1, 2], [3, 4], [5], [6]];
  private readonly fallen: FallenView[] = [];
  private time = 0;
  private damageVersion = -1;
  private burnTimer = 0;
  private fittingsTimer = 0;
  /** Distance to the camera last frame. */
  distance = 0;
  readonly events: ModelEvent[] = [];

  /** `isPlayer`: our own ship keeps full detail and carries the lantern lights. */
  constructor(assets: ShipAssets, readonly vessel: Vessel, private readonly isPlayer: boolean) {
    this.uniforms = makeShipUniforms();
    this.look = lookFor(vessel);
    this.materials = createShipMaterials(assets.tex, this.uniforms, this.look);
    const s = vessel.ship;
    this.root.scale.set(s.sx, s.sy, s.sz);
    this.posed.add(this.root);
    this.posed.matrixAutoUpdate = true;

    const h = assets.hull(this.materials, vessel.cls.id, isPlayer);
    this.hull = h.group;
    this.furniture = h.furniture;
    this.hullLod = h.lod;
    this.root.add(h.group);
    const pivot = new THREE.Group();
    this.rudderPivot = pivot;
    pivot.position.copy(h.rudderPos);
    pivot.quaternion.copy(h.rudderQuat);
    this.rudder = h.rudder;
    pivot.add(this.rudder);
    this.root.add(pivot);

    this.rig = new Rig(this.materials, s.sails, vessel.cls, vessel.damage, assets.art?.rig ?? null);
    this.root.add(this.rig.group);
    this.fittings = new Fittings(this.materials, vessel.battery, vessel.cargo, assets.art?.props ?? null, h.withFittings, h.wheel);
    this.root.add(this.fittings.group);

    if (isPlayer) {
      for (let i = 0; i < this.lightAnchors.length; i++) {
        const l = new THREE.PointLight(new THREE.Color(1.0, 0.62, 0.3), 0, 16, 2);
        l.castShadow = false;
        l.layers.enableAll();
        this.lights.push(l);
        this.root.add(l);
      }
    }
  }

  /** Place at the interpolated sim pose. */
  sync(alpha: number): void {
    const s = this.vessel.ship;
    const o = s.origin(tmpV, alpha);
    this.posed.position.set(o.x, o.y, o.z);
    const q = s.renderRotation(tmpQ, alpha);
    this.posed.quaternion.set(q.x, q.y, q.z, q.w);
    // Only her own placement now (cameras, crew and trackers read it before the
    // frame is drawn); the renderer brings her parts up to date when it draws.
    this.posed.updateMatrix();
    this.posed.matrixWorld.copy(this.posed.matrix);
  }

  /**
   * Back in frame after being left out: masts that went over the side
   * while nobody was looking are already in the water (no splash now).
   */
  wake(): void {
    for (const f of this.fallen) if (f.spar.angle >= 1.44) f.splashed = true;
  }

  /** Drawn as the basic build only (far off)? */
  get low(): boolean {
    return this.lowNow;
  }

  update(dt: number, night: number, pixelAngle: number, wetness: number, camPos: THREE.Vector3): void {
    this.time += dt;
    const v = this.vessel;
    const s = v.ship;
    this.distance = camPos.distanceTo(this.posed.position);
    // Her length on screen, px.
    const px = (36 * s.sx) / Math.max(1, this.distance) / Math.max(1e-7, pixelAngle);
    if (!this.isPlayer) this.setLow(this.lowNow ? px < BASIC_PX_OUT : px < BASIC_PX);
    const far = px < FITTINGS_PX;
    this.rudder.rotation.y = s.rudder;
    this.rig.detail = !far;
    this.rig.update(v.state === 'struck' || v.state === 'captured');
    if (this.rig.pieces !== this.rigPieces) {
      this.rigPieces = this.rig.pieces;
      if (this.lowNow) this.applyLod();
    }
    this.rig.ropes.mesh.visible = !this.lowNow;
    this.fittings.group.visible = !this.lowNow && !far;
    // Deck furniture is lost in the hull's own shading well before that, and
    // a few cables off a lighter hull does.
    if (this.furniture) this.furniture.visible = this.isPlayer || px > FURNITURE_PX;
    if (this.hullLod && !this.isPlayer) {
      const mid = this.midNow ? px < HULL_MID_PX_OUT : px < HULL_MID_PX;
      if (mid !== this.midNow) {
        this.midNow = mid;
        this.hullLod.mid.visible = mid;
        for (const o of this.hullLod.full) o.visible = !mid;
      }
    }
    this.fittingsTimer -= dt;
    if (this.fittings.group.visible && (!far || this.fittingsTimer <= 0)) {
      this.fittings.update(s.helm);
      this.fittingsTimer = 0.25;
    }
    const u = this.uniforms;
    u.uWet.value = wetness;
    u.uDeckWater.value = s.deckWater;
    const lit = v.lights && v.state !== 'sunk' && v.state !== 'sinking';
    u.uNightGlow.value = lit ? night : 0;
    u.uPixelAngle.value = pixelAngle;
    u.uTimeS.value = this.time;
    // Damage is only drawn by the full-detail hull shaders.
    if (!this.lowNow) this.updateDamage(dt);
    this.updateFallen(dt);
    // Lantern light: warm, flickering, only noticeable after dusk.
    this.lightAnchors.forEach((ids, i) => {
      const l = this.lights[i];
      if (!l) return;
      const p = new THREE.Vector3();
      for (const id of ids) p.add(this.fittings.lanternPositions[id]!);
      p.multiplyScalar(1 / ids.length);
      l.position.copy(p);
      const flicker = 0.88 + 0.12 * Math.sin(this.time * 13 + i * 2.1) * Math.sin(this.time * 7.3 + i);
      l.intensity = lit ? (0.15 + night * 4.5) * flicker * (ids.length > 1 ? 1.6 : 1) : 0;
    });
  }

  /** Switch between the full ship and her basic build. */
  private setLow(low: boolean): void {
    if (low === this.lowNow) return;
    this.lowNow = low;
    if (low && !this.lowHull) {
      this.lowMats = createLowMaterials(this.look);
      this.lowHull = cachedBatch('hull:low', (b) => {
        for (const g of lowPolyHull()) b.add('hull', g);
        b.add('deck', lowPolyDeck());
      }, this.lowMats, false, true);
      this.root.add(this.lowHull);
    }
    this.hull.visible = !low;
    this.rudderPivot.visible = !low;
    this.lowHull!.visible = low;
    this.rig.setLow(low);
    this.applyLod();
    // The hull shaders' damage uniforms weren't kept up while far off.
    if (!low) this.damageVersion = -1;
  }

  /**
   * Restyle what's aloft for the current detail: spars, canvas and flags
   * take the plain materials; everything else (tops, hoops, caps, rope,
   * blocks, deadeyes) is hidden. Only meshes this pass hid are shown again.
   */
  private applyLod(): void {
    const lowM = this.lowMats;
    if (!lowM) return;
    const low = this.lowNow;
    const restyle = (o: THREE.Object3D) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      const lm = lowM[m.name];
      if (lm) {
        m.userData.full ??= m.material;
        m.material = low ? lm : (m.userData.full as THREE.Material);
      } else if (low) {
        if (m.visible) {
          m.visible = false;
          m.userData.lodHidden = true;
        }
      } else if (m.userData.lodHidden) {
        m.visible = true;
        m.userData.lodHidden = false;
      }
    };
    this.rig.group.traverse(restyle);
    for (const f of this.fallen) f.group.traverse(restyle);
  }

  /** Feed holes, stove-in rails and fire damage to the hull shaders. */
  private updateDamage(dt: number): void {
    const d = this.vessel.damage;
    const s = this.vessel.ship;
    const u = this.uniforms;
    if (d.version !== this.damageVersion) {
      this.damageVersion = d.version;
      const scale = (s.sx + s.sy) / 2;
      const holes = [...d.holes].sort((a, b) => b.r - a.r).slice(0, MAX_HOLES);
      u.uHoles.value.forEach((h, i) => {
        const src = holes[i];
        if (!src) return void h.set(0, 0, 0, 0);
        // Drawn larger than the bare opening: splintered planking round it.
        const r = (src.r / scale) * 2.2;
        h.set(src.x, src.y, src.z, src.patched > 0.95 ? -r : r);
      });
      const br = [...d.breaches].sort((a, b) => b.w - a.w).slice(0, MAX_BREACHES);
      u.uBreach.value.forEach((b, i) => {
        const src = br[i];
        if (!src) return void b.set(0, 0, 0, 0);
        b.set(src.x, src.w, src.depth, src.side);
      });
    }
    this.burnTimer -= dt;
    if (this.burnTimer <= 0) {
      this.burnTimer = 0.2;
      const cells = this.vessel.fire.cells;
      for (let st = 0; st < BURN_STATIONS; st++) {
        let heat = 0;
        const ch = [0, 0, 0];
        for (const c of cells) {
          if (c.station !== st) continue;
          const burnt = Math.min(1, (1 - c.fuel) * 3);
          if (c.level === 1) ch[c.lane] = Math.max(ch[c.lane]!, burnt);
          else ch[c.lane] = Math.max(ch[c.lane]!, burnt * 0.4);
          heat = Math.max(heat, c.heat);
        }
        u.uBurn.value[st]!.set(ch[0]!, ch[1]!, ch[2]!, heat);
      }
    }
  }

  /** Masts over the side: fall, hang in the water, then get cut away. */
  private updateFallen(dt: number): void {
    const v = this.vessel;
    for (const f of v.fallen) {
      if (this.fallen.some((x) => x.spar === f)) continue;
      const m = mast(f.mast);
      const breakY = f.level === 0 ? (v.damage.masts[f.mast]?.stump ?? f.breakY) : f.level === 1 ? m.lowerTop - 2.4 : m.topmastTop - 1.6;
      const tg = v.cls.sails.some((id) => id.startsWith(f.mast) && id.includes('topgallant'));
      const b = new Batch();
      buildFallenSpar(b, f.mast, breakY, tg, v.cls.sails);
      const inner = b.build(this.materials);
      const px = mastXAt(m, breakY) + (f.level === 0 ? 0 : f.level === 1 ? 0.5 : 0.85);
      inner.position.set(-px, -breakY, 0);
      const pivot = new THREE.Group();
      pivot.position.set(px, breakY, 0);
      pivot.add(inner);
      const group = new THREE.Group();
      group.add(pivot);
      this.root.add(group);
      // One already lying in the water when first drawn made its splash unseen.
      this.fallen.push({ spar: f, group, pivot, cut: 0, splashed: f.angle >= 1.44 });
      if (this.lowNow) this.applyLod();
    }
    for (let i = this.fallen.length - 1; i >= 0; i--) {
      const fv = this.fallen[i]!;
      const f = fv.spar;
      // Upper masts end up hanging down alongside, lower masts lie out
      // over the side with their heads in the water.
      const maxVis = f.level === 0 ? 1.85 : 2.4;
      const a = (f.angle / 1.45) * maxVis;
      fv.pivot.rotation.set(f.side * a, 0, -0.15 * (f.angle / 1.45));
      if (!fv.splashed && f.angle >= 1.44) {
        fv.splashed = true;
        const m = mast(f.mast);
        const len = (m.topgallantTop ?? m.topmastTop) - fv.pivot.position.y;
        const tip = new THREE.Vector3(fv.pivot.position.x, fv.pivot.position.y + Math.cos(maxVis) * len, f.side * Math.sin(maxVis) * len);
        this.root.localToWorld(tip);
        this.events.push({ x: tip.x, y: tip.y, z: tip.z, size: f.level === 0 ? 2.2 : 1.2 });
      }
      if (f.cutAway) {
        // Cut away: she drifts clear and the wreck settles.
        fv.cut += dt;
        fv.group.position.set(-fv.cut * 0.8, -fv.cut * 0.35, f.side * fv.cut * 0.9);
        if (fv.cut > 20) {
          this.root.remove(fv.group);
          this.fallen.splice(i, 1);
        }
      }
    }
  }

  dispose(): void {
    this.posed.removeFromParent();
    for (const m of Object.values(this.materials)) for (const x of materialFamily(m)) x.dispose();
    this.posed.traverse((o) => {
      if ((o as THREE.InstancedMesh).isInstancedMesh) (o as THREE.InstancedMesh).dispose();
    });
  }
}
